import { CateringTranslations } from "./catering-pt-br";

export const CATERING_EN_US: CateringTranslations = {
	title: "Catering",
	description:
		"Manage your events catering, receive proposals, send quotes and monitor everything in one place.",
	createProposal: "Create proposal",
	createProposalMenu: {
		new: "New",
		fromTemplate: "From a template",
	},
	proposalsList: {
		filters: {
			searchPlaceholder: "Search by representative or sender name",
			statusPlaceholder: "All statuses",
		},
		actions: {
			ariaLabel: "Proposal actions",
			viewEdit: "View / Edit proposal",
			view: "View proposal",
			saveAsTemplate: "Save as template",
			download: "Download proposal",
			resend: "Resend proposal",
			delete: "Delete",
		},
		confirmDelete: {
			title: "Delete proposal",
			message:
				"Are you sure you want to delete this proposal? This action cannot be undone.",
			confirm: "Delete",
		},
		feedback: {
			resendSuccess: "Proposal resent successfully.",
			downloadError: "Could not download the PDF.",
			deleteSuccess: "Proposal deleted successfully.",
		},
	},
	createProposalPage: {
		title: "New proposal",
		sidebarSummary: {
			client: "Client",
			duration_one: "{{count}} day",
			duration_other: "{{count}} days",
			totalLabel: "Total investment",
			servicesCount_one: "{{count}} service",
			servicesCount_other: "{{count}} services",
			perParticipant: "{{value}} per participant",
			perDay: "{{value}} per day",
		},
		saveAndAdvance: "Save and continue",
		notifications: {
			createSuccess: "Proposal created successfully!",
			updateSuccess: "Proposal updated successfully!",
		},
		steps: {
			requestDetails: {
				label: "Request details",
				action: "Identify the proposal",
				title: "Request details",
				description: "Enter the identification information for the proposal requester.",
				fields: {
					name: {
						label: "Proposal name",
						placeholder: "Enter the proposal name",
					},
					observation: {
						label: "Observation (optional)",
						placeholder: "Ex.: Minimum booking of 50 pax.",
						counter: "{{count}}/{{max}} characters",
					},
					companyName: {
						label: "Requesting company",
						placeholder: "Enter the company / client name",
					},
					representativeName: {
						label: "Company representative",
						placeholder: "Enter the representative name",
					},
					eventPeriod: {
						label: "Event period",
						placeholder: "DD/MM/YY - DD/MM/YY",
					},
				},
				validation: {
					name: { required: "Enter the proposal name" },
					companyName: { required: "Enter the requesting company" },
					representativeName: { required: "Enter the company representative" },
					eventPeriod: {
						required: "Enter the event period",
						future: "Dates must be in the future",
						endBeforeStart: "End date must be greater than or equal to start",
					},
					observation: { max: "Maximum of {{max}} characters" },
				},
			},
			services: {
				label: "Services",
				action: "Add services and products",
				title: "Services",
				description: "Add the services and products of the proposal.",
				priceUpdates: {
					alert: {
						title: 'Proposal created from the template "[Template name]".',
						message:
							"Some prices were updated since this template was saved. Review the values before sending.",
						link: "View updated products",
					},
					modal: {
						title: "Updated products",
						intro:
							"Some products had their prices changed since this proposal was created from a template. Review the values before sending.",
						sectionTitle: "Changed products",
						removed: "Product removed",
						confirm: "Got it",
					},
				},
				emptyState: {
					title: "No services added yet",
					description: "Use the search bar to find products",
					action: "Add",
				},
				list: {
					counter: "Services added ({{count}})",
					add: "Add",
					total: "Total {{value}}",
					totalCount: "/ {{count}} services",
					fallbackTitle: "Service",
					actions: {
						reorder: "Reorder service",
						edit: "Edit",
						duplicate: "Duplicate",
						delete: "Delete",
					},
					itemsCount: "{{count}} items",
					periodsCount: "{{count}} periods",
					notifications: {
						added: "Service added",
						updated: "Service updated",
						removed: "Service deleted",
						duplicated: "Service duplicated",
					},
					confirmDelete: {
						title: "Delete service",
						message:
							"Are you sure you want to delete this service? This action cannot be undone.",
						messageWithName:
							'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
						confirm: "Delete",
					},
				},
				drawer: {
					title: "New service",
					editTitle: "Edit service",
					steps: {
						info: { label: "Details" },
						products: { label: "Products" },
					},
					actions: {
						advance: "Continue",
						back: "Back",
						save: "Save",
						summary: "{{count}} items",
					},
					products: {
						title: "Add products",
						searchPlaceholder: "Search a product or combo",
						perPax: "/pax",
						empty: "No products found.",
						minSelectionError: "You must select at least 1 product",
						scrollPrev: "Previous categories",
						scrollNext: "Next categories",
						categories: {
							all: "All",
							food: "Food",
							drink: "Drinks",
							combo: "Combos",
						},
					},
					mountable: {
						title: "Choose the options",
						sectionCounter: "{{current}} of {{max}}",
						confirm: "Add",
						remove: "Remove",
						confirmRemove: {
							title: "Remove combo from service?",
							message: "The selections for this combo will be discarded.",
							confirm: "Remove",
						},
					},
					info: {
						title: "Enter the information for the new service",
						fields: {
							name: {
								label: "Group name (optional)",
								placeholder: "Ex.: Catering 1",
							},
							serviceType: {
								label: "Service type",
								placeholder: "Select",
							},
							serviceFormat: {
								label: "Format",
								placeholder: "Select",
							},
							numberOfPeople: {
								label: "Number of people",
								placeholder: "00",
							},
							location: {
								label: "Location or stand",
								placeholder: "Ex. 'Room 5'",
							},
							periodMode: {
								label: "Add schedule by",
								datetime: "Date and time",
								duration: "Duration",
							},
							dates: {
								label: "Dates",
								placeholder: "DD/MM/YYYY",
							},
							startTime: {
								label: "Start time",
								placeholder: "00:00",
							},
							endTime: {
								label: "End time",
								placeholder: "00:00",
							},
							period: {
								add: "Add period",
								removeAria: "Remove period {{index}}",
							},
							duration: {
								label: "Time",
								placeholder: "00",
								unitLabel: "Unit",
								units: {
									minutes: "Minutes",
									hours: "Hours",
								},
							},
							notes: {
								label: "Notes (optional)",
								placeholder: "Ex.: No fried foods or processed meats.",
							},
						},
						validation: {
							serviceTypeId: { required: "Select the service type" },
							serviceFormatId: { required: "Select the format" },
							numberOfPeople: { required: "Enter the number of people" },
							location: { required: "Enter the location or stand" },
							duration: { required: "Enter the duration" },
							period: {
								invalidTime: "Invalid time",
								endBeforeStart: "Must be greater than or equal to start",
								dateRequired: "Enter the date",
							},
							notes: { max: "Maximum of {{max}} characters" },
						},
					},
				},
			},
			sendInfo: {
				label: "Submission info",
				action: "Review and send proposal",
				title: "Review and send proposal",
				description: "Review the data and send the proposal to the client.",
				submit: "Review and send",
				previewEmail: "Preview email",
				confirm: {
					title: "Review before confirming the submission",
					clientLabel: "Client",
					eventStart: "{{date}} start",
					daysTotal_one: "{{count}} day total",
					daysTotal_other: "{{count}} days total",
					investmentLabel: "Total investment",
					productsCount_one: "{{count}} product",
					productsCount_other: "{{count}} products",
					perParticipantSuffix: "per participant",
					perDaySuffix: "per day",
					discountBadge: "Discount of {{value}}",
					additionBadge: "Addition of {{value}}",
					attachmentSize: "974.91 KB",
					attachmentName: "Quote {{proposalName}}.pdf",
					saveAsTemplate: "Save as template",
					cancel: "Cancel",
					send: "Send proposal",
					sendSuccess: "Proposal sent successfully!",
					downloadPdf: "Download PDF",
					sending: {
						pdf: "Generating PDF...",
						email: "Sending email...",
					},
					error: {
						title: "Could not generate the PDF.",
						cta: "Please try again",
						retry: "Try again",
						backToDraft: "Back to draft",
					},
				},
				preview: {
					title: "Review before confirming the submission",
					back: "Back",
					subject: "Your catering quote has arrived",
					attachmentSize: "974.91 KB",
					attachmentName: "Quote {{proposalName}}.pdf",
					replyCta: "Reply to proposal",
					signOff: "Best regards,",
					signatureFallback: "Team",
				},
				fields: {
					totalValue: "Total value",
					adjustment: {
						toggle: "Apply discount or addition",
						valueLabel: "Value",
						valuePlaceholder: "$ 0.00",
						discount: "Discount",
						addition: "Addition",
					},
					mainEmail: {
						label: "Main email for sending",
						placeholder: "maria@email.com",
						tooltip: "Main client email that will receive the proposal.",
					},
					ccEmails: {
						label: "CC emails",
						placeholder: "maria@email.com, joao@email.com",
						tooltip: "Separate multiple emails with a comma.",
					},
					validUntil: {
						label: "Proposal validity",
						placeholder: "MM/DD/YYYY",
					},
					bodyText: {
						label: "Email body text",
						placeholder: "Ex.: Hi! Here is the quote as requested.",
						counter: "characters",
					},
				},
				validation: {
					adjustment: {
						value: {
							required: "Enter the value",
							min: "Value must be greater than zero",
						},
						type: { required: "Select the adjustment type" },
					},
					mainEmail: {
						required: "Enter the main email",
						invalid: "Invalid email",
					},
					ccEmails: { invalid: "One or more emails are invalid" },
					validUntil: {
						required: "Enter the proposal validity",
						future: "The date must be in the future",
					},
					bodyText: { max: "Maximum of {{max}} characters" },
				},
			},
		},
	},
	proposalDetailPage: {
		proposalLabel: "Proposal #{{id}}",
		status: {
			newResponse: "NEW RESPONSE",
		},
		actions: {
			copyLink: "Copy link",
			favorite: "Favorite",
		},
		sidebar: {
			historyTab: "History",
			clientLabel: "Client",
			investmentTotal: "Total investment",
			sendInfoPlaceholder: "Send summary",
			stepLabels: {
				history: "History",
				requestDetails: "Request details",
				services: "Services",
				sendInfo: "Send",
			},
			durationDays_one: "{{count}} day",
			durationDays_other: "{{count}} days",
			servicesCount_one: "{{count}} service",
			servicesCount_other: "{{count}} services",
			perParticipantSuffix: "per participant",
			perDaySuffix: "per day",
			startDateSuffix: "start",
		},
		history: {
			title: "History",
			description: "View your conversation with the client.",
			firstReply: "First reply on {{date}} at {{time}}, {{weekday}}",
			sortAria: "Sort messages",
			loadError: "Could not load the history.",
			message: {
				newTag: "NEW",
				reply: "Reply",
				sendFailed: "SEND FAILED",
				resend: "Resend",
				downloadAria: "Download attachment",
			},
		},
		placeholder: "{{section}} under construction",
	},
};
